import { Link } from "react-router-dom";
import styled from "styled-components";
import { Box, Flex, Text } from "rebass/styled-components";
import { FaArrowRight } from "react-icons/fa";

import { ReactComponent as BasicIcon } from "MVVM/Assets/Avatar/Icons/essentials.svg";
import { ReactComponent as StandardIcon } from "MVVM/Assets/Avatar/Icons/pro.svg";
import { ReactComponent as UnlimitedIcon } from "MVVM/Assets/Avatar/Icons/platinum.svg";

import { IPlan, PLANS } from "../data";
import {
  ACCENT,
  ACCENT_BORDER,
  Section,
  SectionHeading,
  SectionSubheading,
  Underlined,
} from "../../styledComponents";

const Perks = ({ perks }: { perks: string[] }) => (
  <PerkList>
    {perks.map((perk: string) => (
      <li key={perk}>{perk}</li>
    ))}
  </PerkList>
);

export const Plans = () => {
  const { free, paid }: { free: IPlan; paid: IPlan } = PLANS;

  return (
    <Section py={[72, 96, 120]} alignItems="center">
      <SectionHeading textAlign="center">
        {PLANS.headingLead}
        <Underlined>{PLANS.headingUnderlined}</Underlined>
      </SectionHeading>
      <SectionSubheading textAlign="center">
        {PLANS.subheading}
      </SectionSubheading>

      <Columns mt={[8, 9, 9]}>
        <Column>
          <Icons>
            <BasicIcon aria-hidden="true" />
          </Icons>
          <Tier>{free.tier}</Tier>
          <Perks perks={free.perks} />
        </Column>

        <Column className="paid">
          <Icons>
            <StandardIcon aria-hidden="true" />
            <UnlimitedIcon aria-hidden="true" />
          </Icons>
          <Tier>{paid.tier}</Tier>
          <Perks perks={paid.perks} />
          <Upgrade to="/upgrade">
            Upgrade <FaArrowRight aria-hidden="true" />
          </Upgrade>
        </Column>
      </Columns>
    </Section>
  );
};

// Two columns on one surface split by a hairline, rather than two boxes. The
// plans are additive, not a head to head, so framing each in its own card made
// them read as a choice the visitor has to make.
const Columns = styled(Flex)`
  gap: 72px;
  max-width: 1000px;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    flex-direction: column;
    gap: 48px;
  }
`;

const Column = styled(Box)`
  flex: 1 1 50%;
  min-width: 0;
  position: relative;

  &.paid::before {
    background: ${({ theme }) => theme.colors.navy50};
    content: "";
    left: -36px;
    position: absolute;
    top: 4px;
    bottom: 4px;
    width: 1px;
  }

  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    &.paid::before {
      bottom: auto;
      height: 1px;
      left: 0;
      right: 0;
      top: -24px;
      width: auto;
    }
  }
`;

const Icons = styled(Flex)`
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;

  svg {
    height: 62px;
    width: auto;
  }
`;

const Tier = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.01em;

  .paid & {
    color: ${ACCENT};
  }
`;

const PerkList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 14px;
  list-style: none;
  margin: 22px 0 0;
  padding: 0;

  li {
    color: ${({ theme }) => theme.colors.chalk};
    font-size: 18px;
    line-height: 1.5;
    padding-left: 26px;
    position: relative;
  }

  li::before {
    background: ${ACCENT};
    border-radius: 50%;
    content: "";
    height: 8px;
    left: 0;
    position: absolute;
    top: 9px;
    width: 8px;
  }
`;

const Upgrade = styled(Link)`
  align-items: center;
  border: 1px solid ${ACCENT_BORDER};
  border-radius: 10px;
  color: ${ACCENT};
  display: inline-flex;
  font-size: 15px;
  font-weight: 700;
  gap: 8px;
  margin-top: 28px;
  padding: 12px 20px;
  text-decoration: none;
  transition:
    background 0.15s ease,
    transform 0.15s ease;

  svg {
    height: 13px;
    transition: transform 0.15s ease;
    width: 13px;
  }

  :hover {
    background: ${({ theme }) => theme.colors.envyAlpha10};
    color: ${ACCENT};
  }

  :hover svg {
    transform: translateX(3px);
  }
`;
